"use client";

import React, { useEffect, useLayoutEffect, useRef } from "react";
import styles from "./content-card-impact.module.css";
import type { ContentCardImpactReviewPins } from "./content-card-impact.types";
import { withSafeRender } from "../../atoms/safe-render";

/** The block's client half: the pin, the measurement behind the rail's fill,
 *  and the rail itself. "use client" is earned by RUNTIME MEASUREMENT.
 *  ⚠ THE SCROLL WRITES ATTRIBUTES AND ONE CUSTOM PROPERTY, NEVER STATE. */

/** The mark the slides carry so the engine can find them. ⚠ MIRRORED BY HAND
 *  IN content-card-impact.tsx AND NEVER IMPORTED ACROSS THE BOUNDARY: that
 *  import would hand a Server Component a client reference. */
const SLIDE = "[data-cci-slide]";

/** The rail's own positions, which this file both draws and reads. */
const POSITION = "[data-cci-position]";

/** What the reader is told the block is, and what it is called. ⚠ THE BLOCK'S
 *  OWN AND NOT AN AUTHOR'S: no field is published to name any of it. */
const CAROUSEL_ROLE_DESCRIPTION = "carousel";

const CAROUSEL_LABEL = "Featured slides";

/** What the set of positions is for, said once above them rather than repeated
 *  in every position's own name. */
const RAIL_LABEL = "Choose a slide";

/** The one tab stop a slide carries: the place focus can be holding when the
 *  pin takes that slide away, and the place it is handed to on arrival. */
const CTA = `.${styles.cta}`;

/** The setting that stops the section pinning at all. */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** A block held on one slide by a prop rather than by a measurement. ⚠ ITS OWN
 *  VALUE AND NOT THE ATTRIBUTE LEFT OFF, which the stylesheet draws as the
 *  whole list in flow. */
const REVIEW = "review";

/** Below this a write moves nothing and the compositor still pays for it. */
export const PROGRESS_STEP = 0.001;

/** `useLayoutEffect` in the browser and `useEffect` on the server, where the
 *  first warns, so a client render measures before its first paint. */
const useMeasureEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Which slide a whole-section progress of 0 to 1 is showing. ⚠ CLAMPED AND
 *  NEVER WRAPPED: at progress 1 the floor lands one past the end, and the
 *  section has to release on the last slide rather than flash the first. */
export function slideIndexFor(progress: number, count: number): number {
  if (!(count > 0)) {
    return 0;
  }

  return Math.min(Math.floor(clamp01(progress) * count), count - 1);
}

/** How far the showing slide's bar has filled, 0 to 1. Reverse scroll drains it
 *  by the same arithmetic, which is why nothing here remembers a direction. */
export function fillFor(progress: number, count: number): number {
  if (!(count > 0)) {
    return 0;
  }

  return clamp01(clamp01(progress) * count - slideIndexFor(progress, count));
}

/** What a screen reader says the rail is showing. */
export function positionLabel(index: number, count: number): string {
  return `Slide ${index + 1} of ${count}`;
}

/** What one position offers to do, which is the whole of its name: the mark is
 *  4px of paint and carries no wording for a reader to go on. */
export function selectLabel(index: number, count: number): string {
  return `Show slide ${index + 1} of ${count}`;
}

/** Half a pixel past the boundary a position asks to be scrolled to. ⚠ NOT THE
 *  BOUNDARY ITSELF: the slide showing is the floor of the progress, so a
 *  rounded-down scroll position lands on the slide before. */
const SELECT_NUDGE = 0.5;

/** Device-pixel rounding on the spacer's own `svh` screen. ⚠ NOT SLACK FOR
 *  THE PANEL'S `dvh` FLOOR, which from 1280px up it does not cover: see
 *  design-conflicts. */
const FIT_TOLERANCE = 1;

/** What a reader held on a panel has to be able to reach. ⚠ ALSO WHAT IS
 *  WATCHED FOR A REWRAP, which is why the picture is not in here: its column
 *  follows every `dvh` step of the URL bar. */
const REACH = `.${styles.copy}, .${styles.cta}`;

/** The picture's column. Its floor is the least of the picture a pinned slide
 *  may show, and the stylesheet owns the figure. */
const ART = `.${styles.art}`;

/** Where the column's floor ends. ⚠ ITS TOP AND ITS FLOOR, NEVER ITS BOTTOM:
 *  the column fills the panel's `dvh` floor, which the `svh` screen it is
 *  judged against does not cover. */
function floorOf(art: Element): number {
  return art.getBoundingClientRect().top + (parseFloat(getComputedStyle(art).minHeight) || 0);
}

/** Whether everything the reader must reach, and the least of each picture, is
 *  inside the screen the section holds it on. ⚠ ASKED ONLY IN THE PINNED
 *  LAYOUT: the screen is the section's own spacer over the count, not
 *  window.innerHeight. */
export function everythingIsInReach(root: Element, viewport: Element, count: number): boolean {
  const panel = viewport.getBoundingClientRect();
  const screen = (root.getBoundingClientRect().height - panel.height) / count;

  if (!(screen > 0)) {
    return true;
  }

  const feet = [
    ...Array.from(root.querySelectorAll(REACH), element => element.getBoundingClientRect().bottom),
    ...Array.from(root.querySelectorAll(ART), floorOf)
  ];

  /* ⚠ THE WHOLE SECTION, AND MEASURED FROM THE PANEL'S OWN TOP: every slide
     shares one grid cell, and a bottom read off the document moves with every
     scroll. */
  return feet.every(foot => foot - panel.top <= screen + FIT_TOLERANCE);
}

/** Whether the reader has asked for less motion. ⚠ READ INSIDE THE EFFECT AND
 *  NOWHERE ELSE, or server and browser disagree about the markup. Guarded
 *  because jsdom ships no matchMedia. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/** Whether the page is still fetching a web face, so the wording on screen is
 *  in the fallback one. Guarded because jsdom and older webviews ship no
 *  FontFaceSet. */
function fontsLoading(): boolean {
  return document.fonts?.status === "loading";
}

interface ImpactScrollerProps extends ContentCardImpactReviewPins {
  /** The slides, rendered on the server and slotted in unchanged. */
  children: React.ReactNode;
  /** How many positions the rail draws. One per slide, and never a fixed four. */
  count: number;
  /** What the block could not draw at all, written on the server. Absent in a
      production build, and absent is also "nothing to say". */
  problems?: readonly string[];
  /** What the block drew less of. Same channel, lower volume. */
  notices?: readonly string[];
}

/** Content Card Impact's scroller: a tall section holding one sticky screen,
 *  and the rail that reports which slide that screen is showing. */
function ImpactScrollerImpl({
  children,
  count,
  notices,
  pinnedFill,
  pinnedIndex,
  problems
}: ImpactScrollerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const announcerRef = useRef<HTMLDivElement>(null);

  /* Absent and 0 are told apart here, because a story pinning the first slide
     is not a story pinning nothing. */
  const pinned = pinnedIndex !== undefined || pinnedFill !== undefined;
  const startIndex = pinnedIndex === undefined ? 0 : Math.min(Math.max(Math.trunc(pinnedIndex), 0), count - 1);

  /* ⚠ KEYED TO THE MESSAGES AND NOT TO THE ARRAY HOLDING THEM: a field written
     inline hands a new array per render, and every message would repeat. */
  const reported = JSON.stringify([problems ?? [], notices ?? []]);

  useEffect(() => {
    problems?.forEach(problem => console.error(problem));
    notices?.forEach(notice => console.warn(notice));
  }, [reported]);

  /* THE PIN AND THE FILL. ⚠ EXHAUSTIVE-DEPS IS OFF IN THIS REPO, so every
     dependency array here is checked by hand. The motion setting is read here
     rather than held in state: it decides only what is written, never rendered. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const viewport = viewportRef.current;

    if (!root) {
      return undefined;
    }

    /* The width the pin was given up at. ⚠ ONLY A NEW WIDTH ASKS AGAIN: a
       height-only resize is a sliding URL bar, and the motion setting going off
       and on must not pin a reader back either. */
    let givenUpWidth: number | undefined;
    let warned = false;
    let detach: (() => void) | undefined;

    /* The reader's setting and a new width are what build the engine again. */
    const reattach = () => {
      detach?.();
      detach = attach();
    };

    /* ⚠ THE THREE VALUES ARE NOT INTERCHANGEABLE: "false" is a section drawn in
       flow, "review" a story's pin, and the attribute absent a section nothing
       has measured. The gate is in script because foundation.css cannot see it. */
    const attach = (): (() => void) | undefined => {
      const reduced = prefersReducedMotion();
      const rail = railRef.current;
      const announcer = announcerRef.current;
      const slides: Element[] = Array.from(root.querySelectorAll(SLIDE));
      const positions: Element[] = rail === null ? [] : Array.from(rail.querySelectorAll(POSITION));

      /* Which slide is showing. ⚠ WRITTEN ON THE ELEMENTS RATHER THAN RENDERED:
         React re-applies an attribute only when the value IT rendered changes,
         so the initial markup below and these writes do not fight. */
      const mark = (index: number) => {
        root.setAttribute("data-active-index", String(index));

        [slides, positions].forEach(group => {
          group.forEach((element, at) => {
            if (at === index) {
              element.setAttribute("data-active", "true");
              return;
            }

            element.removeAttribute("data-active");
          });
        });

        /* ⚠ ON THE POSITIONS ALONE AND NOT ON THE SLIDES: `aria-current` names
           one of a set of controls, and a hidden slide is not a control. */
        positions.forEach((position, at) => {
          if (at === index) {
            position.setAttribute("aria-current", "true");
            return;
          }

          position.removeAttribute("aria-current");
        });
      };

      /* ⚠ EVERY ATTRIBUTE THE ENGINE WROTE AND NOT ONLY THE PIN'S: a bail that
         wrote `data-pinned` alone leaves the slide a scroll had marked showing,
         so a block asked to hold slide 1 still draws slide 2. */
      const restore = (index: number) => {
        mark(index);
        slides.forEach(slide => slide.removeAttribute("tabindex"));

        if (announcer !== null) {
          announcer.textContent = "";
        }
      };

      /* ⚠ NOTHING LISTENS FOR A WIDTH WHILE REDUCED MOTION HOLDS THE SECTION, so
         a width that moved meanwhile is caught here on the way back. */
      if (givenUpWidth !== window.innerWidth) {
        givenUpWidth = undefined;
      }

      if (givenUpWidth !== undefined && !reduced) {
        restore(startIndex);
        root.setAttribute("data-pinned", "false");

        /* ⚠ THE ONE LISTENER A SECTION IN FLOW HOLDS, AND IT MEASURES NOTHING
           until the width moves: a rotation or a zoom rewraps the wording. */
        const widthChanged = () => {
          if (window.innerWidth !== givenUpWidth) {
            givenUpWidth = undefined;
            reattach();
          }
        };

        window.addEventListener("resize", widthChanged, { passive: true });

        return () => window.removeEventListener("resize", widthChanged);
      }

      if (pinned || reduced || !viewport) {
        restore(startIndex);

        if (reduced) {
          root.setAttribute("data-pinned", "false");
        } else if (pinned) {
          root.setAttribute("data-pinned", REVIEW);
        } else {
          /* ⚠ TAKEN OFF RATHER THAN LEFT WHERE IT WAS: with no panel there is
             nothing measured, which is what the absence means. */
          root.removeAttribute("data-pinned");
        }

        return undefined;
      }

      /* ⚠ THE TRAVEL IS ASKED FOR RATHER THAN DECLARED, so a page whose script
         never runs has no screens of dead scroll under it. */
      root.setAttribute("data-pinned", "true");

      let frame = 0;
      let written = -1;
      let showing = -1;
      let released = false;

      /* Set by a resize, by the wording's own box changing or by the fonts
         landing, read on the next painted frame: the question below walks every
         slide's wording to answer. */
      let reask = false;
      let awaitingFonts = false;

      /* The fonts settling, which a resize does not report. ⚠ GUARDED BECAUSE
         jsdom AND OLDER WEBVIEWS SHIP NO FontFaceSet: there the answer given
         without them stands. One wait at a time, or the mount subscribes twice. */
      const askWhenFontsLand = () => {
        if (!awaitingFonts && typeof document.fonts?.ready?.then === "function") {
          awaitingFonts = true;
          document.fonts.ready.then(() => {
            awaitingFonts = false;

            if (!released) {
              resized();
            }
          });
        }
      };

      /* ⚠ THE ONLY PLACE THE QUESTION IS ASKED, AND ONLY IN THE PINNED LAYOUT,
         which is already drawn, so it is a pure READ. A "no" gives the pin up;
         nothing here gives it back. */
      const stillReaches = (): boolean => {
        if (everythingIsInReach(root, viewport, count)) {
          return true;
        }

        /* ⚠ A FALLBACK FACE IS NOT THE ANSWER: it can stand taller than the web
           face, and a pin given up on it would never come back. */
        if (fontsLoading()) {
          askWhenFontsLand();

          return true;
        }

        givenUpWidth = window.innerWidth;

        /* ⚠ "false" RATHER THAN THE ATTRIBUTE TAKEN OFF: both draw the list in
           flow, but only "false" says it was measured. The warning is the reason
           for a developer; the stacked list is what every environment shows. */
        root.setAttribute("data-pinned", "false");

        if (process.env.NODE_ENV !== "production" && !warned) {
          warned = true;
          console.warn(
            "[ContentCardImpact] a slide's wording, its button or the least of its picture falls below the screen the block would hold the reader on, so the block is drawing every slide in flow instead of pinning. Shorten the copy on the longest slide."
          );
        }

        return false;
      };

      /* ⚠ ASKED BEFORE A SINGLE LISTENER EXISTS: a section that never pins holds
         only the width listener the flow branch above installs. */
      if (!stillReaches()) {
        return attach();
      }

      /* ⚠ WCAG 2.4.3 — HIDING THE OUTGOING SLIDE BLURS WHAT IT HELD, so focus is
         moved rather than left to fall to <body>. preventScroll because the
         scroll chose the slide, and bringing it into view would swap it again. */
      const carryFocus = (arriving: Element) => {
        const control = arriving.querySelector<HTMLElement>(CTA);

        if (control !== null) {
          control.focus({ preventScroll: true });

          return;
        }

        const panel = arriving as HTMLElement;

        /* ⚠ TAKEN BACK OFF WHEN FOCUS LEAVES: left behind, the mark makes a
           full-screen panel focusable for the rest of the page's life. */
        panel.tabIndex = -1;
        panel.addEventListener("blur", () => panel.removeAttribute("tabindex"), {
          once: true
        });
        panel.focus({ preventScroll: true });
      };

      const show = (index: number, stepped: boolean) => {
        /* ⚠ READ BEFORE THE SWAP AND NOT AFTER IT: the write below hides the
           slide, and the element it held has been blurred by then. */
        const held = slides.find(slide => slide.contains(document.activeElement));

        mark(index);

        /* ⚠ THE PLACE ALONE, AND ONLY WHERE THE READER HAS MOVED: the slide
           brings its own headline, and a region handed wording on arrival is
           read by some engines and not others. */
        if (announcer !== null && stepped) {
          announcer.textContent = positionLabel(index, count);
        }

        /* Only where the reader was inside the slide being taken away. Focus
           anywhere else is the reader's own and is never moved. */
        const arriving = slides[index];

        if (held !== undefined && arriving !== undefined && held !== arriving) {
          carryFocus(arriving);
        }
      };

      const read = () => {
        frame = 0;

        /* ⚠ BEFORE THE FILL AND NOT AFTER IT: the fill this frame would write
           belongs to a pin the question may be about to take away. */
        if (reask) {
          reask = false;

          if (!stillReaches()) {
            reattach();

            return;
          }
        }

        /* ⚠ THE ONLY LAYOUT READS ON THE SCROLL PATH, with the writes after
           them. The travel is measured rather than window.innerHeight, because
           the spacer is `svh` and the window follows the URL bar. */
        const box = root.getBoundingClientRect();
        const travel = box.height - viewport.getBoundingClientRect().height;

        if (!(travel > 0)) {
          return;
        }

        const progress = clamp01(-box.top / travel);
        const index = slideIndexFor(progress, count);
        const fill = fillFor(progress, count);

        if (index !== showing) {
          /* The opening read is not a step: -1 is a section nothing has read
             yet, and the slide it lands on is the one the markup carries. */
          show(index, showing !== -1);
          showing = index;
        }

        if (Math.abs(fill - written) < PROGRESS_STEP) {
          return;
        }

        written = fill;

        /* ⚠ ON THE RAIL AND NOT ON THE SECTION: written here a frame
           recalculates the rail alone, and the resting 0 and a story's fill
           both stay on `.root`, out of this write's reach. */
        if (rail !== null) {
          rail.style.setProperty("--cci-progress", fill.toFixed(4));
        }
      };

      /* Coalesced: a wheel or a touch drag fires far more often than the screen
         is painted, and every one of those would otherwise be a read. */
      const schedule = () => {
        if (!frame) {
          frame = requestAnimationFrame(read);
        }
      };

      /* ⚠ THE QUESTION RIDES THE SAME FRAME AS THE READ: asked on the event
         instead it would walk every slide's wording on each step of a sliding
         URL bar. */
      const resized = () => {
        reask = true;
        schedule();
      };

      /* ⚠ THE WORDING'S OWN BOXES AND NOT THE SECTION'S: those move only when
         the wording rewraps, where `.root` also follows every `dvh` step of the
         URL bar. A target's first delivery is its observation starting. */
      const observed = new Set<Element>();
      const rewrapped = (entries: ResizeObserverEntry[]) => {
        let changed = false;

        entries.forEach(({ target }) => {
          changed = changed || observed.has(target);
          observed.add(target);
        });

        if (changed) {
          resized();
        }
      };

      /* ⚠ THE TRIGGER A RESIZE EVENT DOES NOT GIVE: a default font size raised
         under a loaded page moves the button and fires nothing. Guarded because
         jsdom and older webviews ship none. */
      const observer =
        typeof ResizeObserver === "function" ? new ResizeObserver(rewrapped) : undefined;

      /* ⚠ A POSITION ASKS FOR THE SCROLL RATHER THAN SHOWING THE SLIDE ITSELF,
         or the section is drawn on one slide and measured on another. Enter and
         Space come free, because these are real buttons. */
      const select = (event: Event) => {
        const { target } = event;
        const pressed = target instanceof Element ? target.closest(POSITION) : null;
        const index = pressed === null ? -1 : positions.indexOf(pressed);

        if (index < 0) {
          return;
        }

        const box = root.getBoundingClientRect();
        const travel = box.height - viewport.getBoundingClientRect().height;

        if (!(travel > 0)) {
          return;
        }

        window.scrollBy({ top: box.top + (index * travel) / count + SELECT_NUDGE });
      };

      /* Everything the engine holds, handed back. ⚠ ONE PATH FOR BOTH ENDINGS:
         the section going, and the pin given up under a reader. */
      const release = () => {
        released = true;

        observer?.disconnect();
        rail?.removeEventListener("click", select);
        window.removeEventListener("scroll", schedule);
        window.removeEventListener("resize", resized);

        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }

        /* ⚠ THE MARK A LANDING LEFT ON A PANEL GOES WITH THE ENGINE: in flow
           nothing is left holding a tab stop it cannot lose. */
        slides.forEach(slide => slide.removeAttribute("tabindex"));

        /* ⚠ OFF THE RAIL, WHICH IS WHERE THE MEASUREMENT WROTE IT: taken off
           `.root` it would wipe the fill a story pinned there. */
        rail?.style.removeProperty("--cci-progress");
      };

      read();
      rail?.addEventListener("click", select);
      window.addEventListener("scroll", schedule, { passive: true });

      /* ⚠ AND A ROTATION IS A RESIZE: `orientationchange` is deprecated and
         fires before the new layout has settled. */
      window.addEventListener("resize", resized, { passive: true });

      root.querySelectorAll(REACH).forEach(element => observer?.observe(element));

      /* ⚠ THE MOUNT MAY HAVE MEASURED THE FALLBACK FACE: `fonts.css` swaps with
         no `size-adjust`, so the web face can move the button afterwards. */
      askWhenFontsLand();

      return () => {
        release();

        /* ⚠ TAKEN OFF RATHER THAN SET TO "false": what a page carries before
           anything has measured it is the absence, and the section is going. */
        root.removeAttribute("data-pinned");
      };
    };

    detach = attach();

    const stop = () => detach?.();

    if (typeof window.matchMedia !== "function") {
      return stop;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);

    /* ⚠ A MediaQueryList IS NOT AN EventTarget ON SAFARI 13 AND OLDER WEBVIEWS,
       and calling `addEventListener` on it throws. Remove the deprecated pair
       when a browserslist rules those engines out. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", reattach);

      return () => {
        query.removeEventListener("change", reattach);
        stop();
      };
    }

    query.addListener(reattach);

    return () => {
      query.removeListener(reattach);
      stop();
    };
    /* ⚠ THE HELD SLIDE IS A DEPENDENCY THOUGH NOTHING MEASURES IT: moving a
       pin between slides leaves `pinned` true, and without it the bail above
       never runs again. */
  }, [count, pinned, startIndex]);

  if (!(count > 0)) {
    return null;
  }

  return (
    /* ⚠ THE SCREENS OF SCROLL ARE A COUNT RATHER THAN A HEIGHT: the stylesheet
       turns it into travel, so the two cannot disagree about a screen. */
    <div
      /* ⚠ AND ONE SLIDE IS NOT A CAROUSEL: with nowhere to step to, the name
         promises a set of controls that has nothing to choose between. */
      aria-label={count > 1 ? CAROUSEL_LABEL : undefined}
      aria-roledescription={count > 1 ? CAROUSEL_ROLE_DESCRIPTION : undefined}
      className={styles.root}
      data-active-index={startIndex}
      data-pinned={pinned ? REVIEW : undefined}
      ref={rootRef}
      /* A roledescription needs a role to rename, and group is the APG
         pattern's own choice for the box a carousel's slides sit in. */
      role={count > 1 ? "group" : undefined}
      style={
        {
          "--cci-panels": count,
          ...(pinnedFill === undefined ? {} : { "--cci-progress": clamp01(pinnedFill) })
        } as React.CSSProperties
      }
    >
      {/* ⚠ RENDERED EMPTY FROM THE FIRST PAINT, AND OUTSIDE THE SCREEN THE PIN
          CLIPS: a region added with its content already in it is read by some
          engines and not others. */}
      <div aria-atomic="true" aria-live="polite" className="dds-sr-only" ref={announcerRef} />

      <div className={styles.viewport} ref={viewportRef}>
        {/* ⚠ A SET OF CONTROLS AND NOT A PROGRESS BAR, whose children are
            presentational. The target meets SC 2.5.8 across only — 10 down
            against 24, a recorded divergence. */}
        <div aria-label={RAIL_LABEL} className={styles.rail} ref={railRef} role="group">
          {Array.from({ length: count }, (_unused, index) => (
            <button
              aria-current={index === startIndex ? "true" : undefined}
              aria-label={selectLabel(index, count)}
              className={styles.position}
              data-active={index === startIndex ? "true" : undefined}
              data-cci-position=""
              key={index}
              type="button"
            >
              {/* The fill the scroll drives, on the showing position alone. */}
              <span className={styles.fill} />
            </button>
          ))}
        </div>

        <div className={styles.panels}>{children}</div>
      </div>
    </div>
  );
}
export const ImpactScroller = withSafeRender(ImpactScrollerImpl, "ImpactScroller");
